import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/ui/Button';
import { getBlobStats, listBlobs, BlobItem } from '../api/azureLibrary';
import { useAuth } from '../hooks/useAuth';
import { useSubscription } from '../hooks/useSubscription';
import { getFavorites } from '../services/favoritesService';
import { useTheme } from '../contexts/ThemeContext';
import { useMediaQuery } from '../hooks/useMediaQuery';

type UploadItem = {
  name: string;
  size: number;
  type: string;
  uploadedAt: string;
  thumbnail?: string;
};

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

async function handleDownloadFromPreview(file: BlobItem) {
  try {
    const fileName = file.name.split('/').pop() || file.name;
    const response = await fetch(file.url);
    const blobData = await response.blob();
    const url = window.URL.createObjectURL(blobData);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Download failed:', error);
    alert('Failed to download file');
  }
}

export default function Home() {
  const { theme } = useTheme();
  const { isMobile } = useMediaQuery();
  const { userId } = useAuth();
  const { subscription } = useSubscription();
  const navigate = useNavigate();
  const [favoriteFiles, setFavoriteFiles] = useState<BlobItem[]>([]);
  const [previewFile, setPreviewFile] = useState<BlobItem | null>(null);
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [stats, setStats] = useState({
    totalFiles: 0,
    totalSize: 0,
    filesThisWeek: 0,
    filesByType: {} as { [key: string]: number }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();

    const handleUpdate = () => loadData();
    window.addEventListener('uploadsUpdated', handleUpdate);

    return () => window.removeEventListener('uploadsUpdated', handleUpdate);
  }, [userId]);

  async function loadData() {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      const stored = localStorage.getItem('recentUploads');
      if (stored) {
        setUploads(JSON.parse(stored));
      }

      const blobStats = await getBlobStats(userId);
      setStats(blobStats);

      const favFileNames = getFavorites(userId);
      if (favFileNames.length > 0) {
        const allBlobs = await listBlobs(userId);
        const favBlobs = allBlobs.filter(blob => favFileNames.includes(blob.name));
        setFavoriteFiles(favBlobs.slice(0, 6));
      }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  }

  // Use quota from subscription — falls back to 50 GB if not loaded yet
  const storageQuota = subscription.quota?.storageBytes ?? (50 * 1024 * 1024 * 1024);
  const storageQuotaGB = subscription.quota?.storageGB ?? 50;
  const planName = subscription.quota?.planName ?? 'Sky One';
  const storagePercent = (stats.totalSize / storageQuota) * 100;

  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ color: theme.colors.textPrimary, fontSize: '32px', marginBottom: '8px' }}>
          Welcome back! 👋
        </h1>
        <p style={{ color: theme.colors.textSecondary, fontSize: '16px' }}>
          Here's what's happening with your files
        </p>
      </div>

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: isMobile ? '12px' : '20px',
        marginBottom: isMobile ? '24px' : '32px'
      }}>
        <StatCard
          title="Total Files"
          value={loading ? '...' : stats.totalFiles.toString()}
          icon="📁"
          color={theme.colors.brand}
          theme={theme}
        />
        <StatCard
          title="Storage Used"
          value={loading ? '...' : formatFileSize(stats.totalSize)}
          subtitle={`of ${storageQuotaGB} GB (${planName})`}
          icon="💾"
          color={theme.colors.accent}
          theme={theme}
        />
        <StatCard
          title="This Week"
          value={loading ? '...' : stats.filesThisWeek.toString()}
          subtitle="files uploaded"
          icon="📈"
          color={theme.colors.brand}
          theme={theme}
        />
        <StatCard
          title="File Types"
          value={loading ? '...' : Object.keys(stats.filesByType).length.toString()}
          subtitle="different types"
          icon="🏷️"
          color={theme.colors.accent}
          theme={theme}
        />
      </div>

      {/* Main Content Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr',
        gap: isMobile ? '16px' : '20px',
        marginBottom: isMobile ? '24px' : '32px'
      }}>
        {/* Favorites Section */}
        {favoriteFiles.length > 0 && (
          <div style={{ marginBottom: '32px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <h2 style={{
                color: theme.colors.textPrimary,
                fontSize: '20px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                ⭐ Favorite Files
              </h2>
              <button
                onClick={() => navigate('/library')}
                style={{
                  background: 'transparent',
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: '6px',
                  padding: '8px 16px',
                  color: theme.colors.brand,
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = `${theme.colors.brand}15`;
                  e.currentTarget.style.borderColor = theme.colors.brand;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = theme.colors.border;
                }}
              >
                View All →
              </button>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(auto-fill, minmax(140px, 1fr))' : 'repeat(auto-fill, minmax(180px, 1fr))',
              gap: isMobile ? '12px' : '16px'
            }}>
              {favoriteFiles.map((file, index) => (
                <div
                  key={index}
                  onClick={() => setPreviewFile(file)}
                  style={{
                    background: theme.colors.bgSecondary,
                    borderRadius: '12px',
                    overflow: 'hidden',
                    border: `1px solid ${theme.colors.border}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = `0 8px 16px ${theme.colors.brand}30`;
                    e.currentTarget.style.borderColor = theme.colors.brand;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = theme.colors.border;
                  }}
                >
                  <div style={{
                    height: '140px',
                    background: file.contentType.includes('image')
                      ? '#000'
                      : theme.gradients.card,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                  }}>
                    {file.contentType.includes('image') ? (
                      <img
                        src={file.url}
                        alt={file.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <span style={{ fontSize: '48px' }}>
                        {getFileIcon(file.contentType)}
                      </span>
                    )}
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      background: `${theme.colors.accent}E6`,
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px'
                    }}>
                      ⭐
                    </div>
                  </div>

                  <div style={{ padding: '12px' }}>
                    <div style={{
                      color: theme.colors.textPrimary,
                      fontSize: '13px',
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }} title={file.name.split('/').pop()}>
                      {file.name.split('/').pop()}
                    </div>
                    <div style={{ color: theme.colors.textTertiary, fontSize: '11px', marginTop: '4px' }}>
                      {formatFileSize(file.size)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Uploads */}
        <div style={{
          background: theme.colors.bgSecondary,
          borderRadius: '12px',
          padding: '24px',
          border: `1px solid ${theme.colors.border}`
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h2 style={{ color: theme.colors.textPrimary, fontSize: '20px', fontWeight: 600 }}>
              Recent Uploads
            </h2>
            <button
              onClick={() => navigate('/library')}
              style={{
                background: 'transparent',
                border: 'none',
                color: theme.colors.brand,
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.accent}
              onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.brand}
            >
              View All →
            </button>
          </div>

          {uploads.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: theme.colors.textSecondary }}>
              <p style={{ fontSize: '48px', marginBottom: '16px' }}>📤</p>
              <p style={{ marginBottom: '20px' }}>No uploads yet</p>
              <Button variant="primary" onClick={() => navigate('/uploads')}>
                Upload Your First File
              </Button>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(auto-fill, minmax(120px, 1fr))' : 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: isMobile ? '12px' : '16px'
            }}>
              {uploads.slice(0, 8).map((upload, index) => (
                <RecentUploadCard key={index} upload={upload} theme={theme} />
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions & Storage */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Quick Actions */}
          <div style={{
            background: theme.colors.bgSecondary,
            borderRadius: '12px',
            padding: '24px',
            border: `1px solid ${theme.colors.border}`
          }}>
            <h3 style={{ color: theme.colors.textPrimary, fontSize: '18px', marginBottom: '16px', fontWeight: 600 }}>
              Quick Actions
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Button variant="primary" onClick={() => navigate('/uploads')}>
                📤 Upload Files
              </Button>
              <Button variant="secondary" onClick={() => navigate('/library')}>
                📚 Browse Library
              </Button>
            </div>
          </div>

          {/* Storage Usage */}
          <div style={{
            background: theme.colors.bgSecondary,
            borderRadius: '12px',
            padding: '24px',
            border: `1px solid ${theme.colors.border}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <h3 style={{ color: theme.colors.textPrimary, fontSize: '18px', fontWeight: 600 }}>
                Storage Usage
              </h3>
              <span style={{
                background: `${theme.colors.brand}20`,
                border: `1px solid ${theme.colors.brand}40`,
                borderRadius: '8px',
                padding: '2px 10px',
                color: theme.colors.brand,
                fontSize: '12px',
                fontWeight: 600,
              }}>
                {planName}
              </span>
            </div>
            <div style={{
              background: theme.colors.bgPrimary,
              height: '12px',
              borderRadius: '6px',
              overflow: 'hidden',
              marginBottom: '12px'
            }}>
              <div style={{
                width: `${Math.min(storagePercent, 100)}%`,
                height: '100%',
                background: storagePercent > 90
                  ? '#e74c3c'
                  : storagePercent > 70
                  ? '#f39c12'
                  : theme.gradients.brand,
                transition: 'width 0.3s ease'
              }} />
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              color: theme.colors.textSecondary,
              fontSize: '14px'
            }}>
              <span>{formatFileSize(stats.totalSize)}</span>
              <span>{storagePercent.toFixed(1)}%</span>
            </div>
            <p style={{ color: theme.colors.textTertiary, fontSize: '12px', marginTop: '8px' }}>
              {formatFileSize(storageQuota - stats.totalSize)} of {storageQuotaGB} GB remaining
            </p>
          </div>

          {/* File Types Breakdown */}
          {Object.keys(stats.filesByType).length > 0 && (
            <div style={{
              background: theme.colors.bgSecondary,
              borderRadius: '12px',
              padding: '24px',
              border: `1px solid ${theme.colors.border}`
            }}>
              <h3 style={{ color: theme.colors.textPrimary, fontSize: '18px', marginBottom: '16px', fontWeight: 600 }}>
                File Types
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {Object.entries(stats.filesByType)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 5)
                  .map(([type, count]) => (
                    <div key={type} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: theme.colors.textPrimary, fontSize: '14px', textTransform: 'capitalize' }}>
                        {type}
                      </span>
                      <span style={{
                        background: theme.colors.bgPrimary,
                        color: theme.colors.textSecondary,
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 600
                      }}>
                        {count}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {previewFile && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            background: 'rgba(0,0,0,0.95)', display: 'flex', justifyContent: 'center',
            alignItems: 'center', zIndex: 9999
          }}
          onClick={() => setPreviewFile(null)}
        >
          <div
            style={{
              background: theme.colors.bgSecondary, padding: '20px', borderRadius: '12px',
              maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto', position: 'relative',
              border: `1px solid ${theme.colors.border}`
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setPreviewFile(null)}
              style={{
                position: 'absolute', top: '16px', right: '16px', background: 'rgba(0,0,0,0.7)',
                border: 'none', color: '#fff', fontSize: '24px', cursor: 'pointer',
                width: '40px', height: '40px', borderRadius: '50%', display: 'flex',
                alignItems: 'center', justifyContent: 'center', zIndex: 10000, transition: 'background 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = theme.colors.brand}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.7)'}
            >
              ✕
            </button>

            <h2 style={{ color: theme.colors.textPrimary, marginBottom: '20px', paddingRight: '50px' }}>
              {previewFile.name.split('/').pop()}
            </h2>

            {(previewFile.contentType.includes('image') || previewFile.name.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i)) && (
              <img src={previewFile.url} alt={previewFile.name}
                style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: '8px' }} />
            )}

            {previewFile.contentType === 'application/pdf' && (
              <iframe src={previewFile.url} style={{ width: '80vw', height: '80vh', border: 'none', borderRadius: '8px' }}
                title="PDF Preview" />
            )}

            {!previewFile.contentType.includes('image') &&
              previewFile.contentType !== 'application/pdf' &&
              !previewFile.name.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i) && (
                <div style={{ padding: '40px', textAlign: 'center' }}>
                  <p style={{ color: theme.colors.textSecondary, marginBottom: '20px', fontSize: '16px' }}>
                    📄 No preview available for this file type.
                  </p>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                    <button
                      onClick={() => handleDownloadFromPreview(previewFile)}
                      style={{
                        padding: '12px 24px', background: theme.gradients.brand, border: 'none',
                        borderRadius: '6px', color: theme.colors.textPrimary, cursor: 'pointer',
                        fontSize: '14px', fontWeight: 600, transition: 'opacity 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    >
                      📥 Download File
                    </button>
                    <button
                      onClick={() => setPreviewFile(null)}
                      style={{
                        padding: '12px 24px', background: theme.colors.bgPrimary,
                        border: `1px solid ${theme.colors.border}`, borderRadius: '6px',
                        color: theme.colors.textPrimary, cursor: 'pointer', fontSize: '14px',
                        fontWeight: 600, transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = theme.colors.brand;
                        e.currentTarget.style.background = `${theme.colors.brand}15`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = theme.colors.border;
                        e.currentTarget.style.background = theme.colors.bgPrimary;
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ title, value, subtitle, icon, color, theme }: {
  title: string; value: string; subtitle?: string; icon: string; color: string; theme: any;
}) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: theme.gradients.card, borderRadius: '16px', padding: '24px',
        border: `1px solid ${theme.colors.border}`, borderTop: `4px solid ${theme.colors.brand}`,
        position: 'relative', overflow: 'hidden', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: isHovered ? `0 12px 24px ${theme.colors.brand}40` : '0 4px 12px rgba(0,0,0,0.2)',
      }}
    >
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: '40%',
        background: 'linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0) 100%)',
        pointerEvents: 'none'
      }} />
      <div style={{ position: 'absolute', top: '-20px', right: '-20px', fontSize: '80px', opacity: 0.08 }}>
        {icon}
      </div>
      <div style={{ position: 'relative', zIndex: 1 }}>
        <p style={{ color: theme.colors.textSecondary, fontSize: '14px', marginBottom: '8px' }}>{title}</p>
        <p style={{ color: theme.colors.brand, fontSize: '32px', fontWeight: 700, marginBottom: '4px' }}>{value}</p>
        {subtitle && <p style={{ color: theme.colors.textTertiary, fontSize: '12px' }}>{subtitle}</p>}
      </div>
    </div>
  );
}

function RecentUploadCard({ upload, theme }: { upload: UploadItem; theme: any }) {
  const isImage = upload.type.includes('image');
  const hasThumb = !!upload.thumbnail;

  return (
    <div
      style={{
        background: theme.colors.bgSecondary, borderRadius: '8px', overflow: 'hidden',
        cursor: 'pointer', transition: 'all 0.2s ease', border: `1px solid ${theme.colors.border}`
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.borderColor = theme.colors.brand;
        e.currentTarget.style.boxShadow = `0 4px 12px ${theme.colors.brand}30`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.borderColor = theme.colors.border;
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{
        height: '100px', background: isImage && hasThumb ? '#000' : theme.gradients.card,
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        {isImage && hasThumb ? (
          <img src={upload.thumbnail} alt={upload.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ fontSize: '32px' }}>{getFileIcon(upload.type)}</div>
        )}
      </div>
      <div style={{ padding: '12px' }}>
        <p style={{
          color: theme.colors.textPrimary, fontSize: '12px', fontWeight: 500,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '4px'
        }} title={upload.name}>
          {upload.name}
        </p>
        <p style={{ color: theme.colors.textTertiary, fontSize: '11px' }}>
          {formatTimeAgo(upload.uploadedAt)}
        </p>
      </div>
    </div>
  );
}

function getFileIcon(type: string): string {
  const lowerType = type.toLowerCase();
  if (lowerType.includes('image')) return '🖼️';
  if (lowerType.includes('pdf')) return '📄';
  if (lowerType.includes('video')) return '🎥';
  if (lowerType.includes('audio')) return '🎵';
  if (lowerType.includes('zip') || lowerType.includes('rar') || lowerType.includes('7z')) return '📦';
  if (lowerType.includes('word') || lowerType.includes('document')) return '📝';
  if (lowerType.includes('excel') || lowerType.includes('sheet')) return '📊';
  if (lowerType.includes('text') || lowerType.includes('txt')) return '📝';
  if (lowerType.includes('code') || lowerType.includes('javascript') || lowerType.includes('typescript')) return '💻';
  return '📄';
}

function formatTimeAgo(date: Date | string): string {
  try {
    const uploadDate = date instanceof Date ? date : new Date(date);
    if (isNaN(uploadDate.getTime())) return 'Unknown';
    const now = new Date();
    const diff = now.getTime() - uploadDate.getTime();
    if (diff < 0) return 'Just now';
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    if (months > 0) return `${months} month${months > 1 ? 's' : ''} ago`;
    if (weeks > 0) return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    return 'Just now';
  } catch (error) {
    return 'Unknown';
  }
}
